import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';

const MarkdownRenderer = ({ content, className = '', inline = false }) => {
    const Wrapper = inline ? 'span' : 'div';

    // Custom components configuration
    const components = {
        p: ({ node, ...props }) => inline ? <span {...props} /> : <p className="mb-3 last:mb-0 leading-relaxed" {...props} />,
        ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
        ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />,
        li: ({ node, ...props }) => <li className="pl-1" {...props} />,
        h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
        h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-3 mb-2" {...props} />,
        h3: ({ node, ...props }) => <h3 className="text-base font-bold mt-2 mb-1" {...props} />,
        blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-4 py-2 italic text-gray-700 my-3 rounded-r" {...props} />
        ),
        code: ({ node, inline: isCodeInline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            return !isCodeInline ? (
                <div className="my-3 rounded-lg overflow-hidden border border-gray-700 bg-[#1e1e1e] text-[#d4d4d4]">
                    <div className="px-4 py-1.5 bg-[#2d2d2d] text-xs font-mono text-gray-400 border-b border-gray-700 flex justify-between items-center">
                        <span>{match?.[1] || 'code'}</span>
                    </div>
                    <div className="p-4 overflow-x-auto font-mono text-sm leading-relaxed whitespace-pre">
                        <code className={className} {...props}>
                            {children}
                        </code>
                    </div>
                </div>
            ) : (
                <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-500 font-mono text-xs" {...props}>
                    {children}
                </code>
            );
        },
        table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4 border border-gray-200 rounded-lg shadow-sm">
                <table className="min-w-full divide-y divide-gray-200" {...props} />
            </div>
        ),
        thead: ({ node, ...props }) => <thead className="bg-gray-50 text-gray-700" {...props} />,
        th: ({ node, ...props }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider border-b border-gray-200" {...props} />,
        tbody: ({ node, ...props }) => <tbody className="bg-white divide-y divide-gray-200 text-gray-700" {...props} />,
        tr: ({ node, ...props }) => <tr className="hover:bg-gray-50 transition-colors even:bg-gray-50/50" {...props} />,
        td: ({ node, ...props }) => <td className="px-4 py-3 text-sm whitespace-normal border-b border-gray-100" {...props} />,
        a: ({ node, ...props }) => <a className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
    };

    return (
        <Wrapper className={`markdown-content ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex, rehypeRaw]}
                components={components}
                unwrapDisallowed={inline}
                disallowedElements={inline ? ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote'] : []}
            >
                {content}
            </ReactMarkdown>
        </Wrapper>
    );
};

export default MarkdownRenderer;
